import { useEffect, useState } from "react";
import { decryptLinkPayload, openLinkSecrets, totpNow } from "../crypto";
import { api, errMsg } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { fmtIn } from "../lib/format";
import { MONO_KEYS, TYPES } from "../lib/itemTypes";
import type { LinkPayload } from "../lib/types";
import { Button, Field, IconButton, Input } from "../ui/ds";
import { Icon } from "../ui/Icon";
import { useToast } from "../state/store";
import { GateCard } from "./GateCard";

interface Meta {
  state: "active" | "expired" | "used" | "revoked" | "missing";
  passphrase: boolean;
  passSalt: string;
  expiresAt: number;
  viewsLeft: number;
}

/** Public page for a one-time link: /s/<id>#<key>. Decrypts in the browser. */
export function ShareOpen({ id, fragment }: { id: string; fragment: string }) {
  const toast = useToast();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [item, setItem] = useState<LinkPayload | null>(null);
  const [viewsLeft, setViewsLeft] = useState(0);
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    // Drop the key from the address bar and history as soon as we've read it.
    window.history.replaceState(null, "", window.location.pathname);
    api<Meta>(`/share/${encodeURIComponent(id)}`)
      .then(setMeta)
      .catch((e) => setErr(errMsg(e)));
  }, [id]);

  const totpSecret = item ? (item.type === "totp" ? item.fields.find((f) => f.k === "Setup key")?.v : item.totp) : undefined;
  useEffect(() => {
    if (!totpSecret) return;
    const tick = () => void totpNow(totpSecret).then(setCode);
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [totpSecret]);

  const open = async () => {
    if (!meta) return;
    if (!fragment) return setErr("This link is incomplete. Copy the whole link and try again.");
    if (meta.passphrase && !pass.trim()) return setErr("Enter the passphrase you were sent.");
    setBusy(true);
    setErr("");
    try {
      const { key, proof } = await openLinkSecrets(fragment, meta.passphrase ? pass : null, meta.passSalt);
      const res = await api<{ enc: string; viewsLeft: number }>(`/share/${encodeURIComponent(id)}/open`, { body: { proof } });
      setItem(await decryptLinkPayload<LinkPayload>(key, res.enc));
      setViewsLeft(res.viewsLeft);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = (v: string, what: string) => void copyText(v, true).then((ok) => ok && toast(`${what} copied`, "Clears from your clipboard in 30s."));

  if (item) {
    return (
      <GateCard
        eyebrow="Shared by Agile Software"
        title={item.title}
        sub={viewsLeft > 0 ? `This link can be opened ${viewsLeft} more ${viewsLeft === 1 ? "time" : "times"}.` : "This was the last view. Copy what you need before closing this page."}
        wide
      >
        <div className="detail-type" style={{ marginTop: -16, marginBottom: 16 }}>
          <Icon name={TYPES[item.type]?.icon ?? "file"} size={14} style={{ color: "var(--deep-teal)" }} />
          <span>{TYPES[item.type]?.label}</span>
        </div>
        <div className="sheet is-stacked">
          {item.fields.map((f, i) => (
            <div className="sheet-row" key={i}>
              <div className="sheet-label">{f.k}</div>
              <div className={`sheet-value${f.s || f.m || MONO_KEYS.has(f.k) ? " is-mono" : ""}${f.m ? " is-multi" : ""}`}>
                {f.s && !revealed[i] ? "•".repeat(12) : f.v}
              </div>
              <div className="sheet-actions">
                {f.s ? (
                  <IconButton icon={revealed[i] ? "eye-off" : "eye"} label={revealed[i] ? "Hide" : "Reveal"} variant="ghost" size="sm" onClick={() => setRevealed((r) => ({ ...r, [i]: !r[i] }))} />
                ) : null}
                <IconButton icon="copy" label="Copy" variant="ghost" size="sm" onClick={() => copy(f.v, f.k)} />
              </div>
            </div>
          ))}
          {code ? (
            <div className="sheet-row">
              <div className="sheet-label">2FA code</div>
              <div className="totp-code">{`${code.slice(0, 3)} ${code.slice(3)}`}</div>
              <div className="sheet-actions">
                <IconButton icon="copy" label="Copy code" variant="ghost" size="sm" onClick={() => copy(code, "2FA code")} />
              </div>
            </div>
          ) : null}
        </div>
      </GateCard>
    );
  }

  const dead = meta && meta.state !== "active";
  return (
    <GateCard
      eyebrow="Shared by Agile Software"
      title={dead ? "This link has stopped working" : "Someone shared a credential with you"}
      sub={
        !meta
          ? err || "Checking the link…"
          : meta.state === "missing"
            ? "This link doesn't exist. Check you copied all of it."
            : meta.state === "used"
              ? "It's been opened as many times as allowed. Ask the sender for a new one."
              : meta.state === "revoked"
                ? "The sender revoked it. Ask them for a new one."
                : meta.state === "expired"
                  ? "It expired. Ask the sender for a new one."
                  : `It's decrypted on this device. Expires in ${fmtIn(meta.expiresAt - Date.now() / 1000)} · ${meta.viewsLeft} ${meta.viewsLeft === 1 ? "view" : "views"} left.`
      }
      foot="Agile Vault · end-to-end encrypted one-time link"
    >
      {meta && !dead ? (
        <form
          className="gate-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void open();
          }}
        >
          {meta.passphrase ? (
            <Field label="Passphrase" hint="The sender gave you this separately, e.g. by phone or text.">
              <Input value={pass} mono autoFocus autoComplete="off" onChange={(v) => { setPass(v); setErr(""); }} placeholder="three-words-here" />
            </Field>
          ) : null}
          <Button type="submit" fullWidth arrow busy={busy}>
            {meta.viewsLeft === 1 ? "Open (uses the last view)" : "Open"}
          </Button>
          {err ? <div className="gate-error" role="alert">{err}</div> : null}
        </form>
      ) : null}
    </GateCard>
  );
}
